import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from '@/utils/analytics'
import { useForm } from '@tanstack/react-form'
import { ValidationError } from '@tanstack/react-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import SearchableSelect from '@/components/SearcheableSelect'
import ProfilePicture from '@/components/ProfilePicture'
import { useAppStore } from '../../store'
import { useTranslation } from '@/i18n'

const NEXT_STEP_URL = '/onboarding/questions'

type ErrorMessageProps = {
  errors?: ValidationError[]
}

type Country = {
  name: string
  code: string
}

const ErrorMessage = ({ errors }: ErrorMessageProps) => (
  <span className="text-red-600 text-sm">{errors && errors.join(', ')}</span>
)

const BasicDetails = () => {
  const { t } = useTranslation()
  const {
    countries,
    getCountries,
    companyName,
    country,
    companySize,
    updateFormDetails,
    createOrganization,
    user,
    refreshUserSession,
  } = useAppStore()

  const navigate = useNavigate()

  useEffect(() => {
    const initializePage = async () => {
      await refreshUserSession()
      // Only track after session refresh confirms user has no tenant
      const currentUser = useAppStore.getState().user
      if (!currentUser?.tenant_id) {
        trackEvent('onboarding-started', { source: 'signup' })
        trackEvent('onboarding-step_viewed', { step_name: 'basic_details', step_index: 0 })
      }
    }

    initializePage()
    getCountries()
  }, [getCountries, refreshUserSession])

  useEffect(() => {
    if (user && user.tenant_id) {
      navigate(NEXT_STEP_URL, { replace: true })
    }
  }, [user, navigate])

  const form = useForm({
    defaultValues: {
      companyName,
      country,
      companySize,
    },
    onSubmit: async ({ value }) => {
      try {
        updateFormDetails(value)
        await createOrganization(
          value.companyName,
          value.country,
          value.companySize
        )
        trackEvent('onboarding-org_completed', {
          company_size: value.companySize?.toString() || '',
          country: value.country,
        })
        trackEvent('auth-signup_completed', {})
        navigate(NEXT_STEP_URL)
      } catch (error) {
        console.error('Failed to create organization:', error)
      }
    },
  })

  return (
    <div className="w-[60%] h-full flex justify-center flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="font-bold text-2xl text-gray-600 dark:text-gray-400">
          {t('account:onboarding.page_title')}
        </h2>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center gap-3">
          <ProfilePicture user={user} />
          <div className="flex flex-col">
            <h4 className="text-xl">{user?.name}</h4>
            <span className="text-md text-gray-500 dark:text-dark-text-muted">{user?.email}</span>
          </div>
        </div>
      </div>
      <p className="text-gray-500 dark:text-dark-text-muted text-lg">
        {t('account:onboarding.page_description')}
      </p>
      <form
        className="flex bg-white dark:bg-dark-card flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <form.Field
          name="companyName"
          validators={{
            onChange: ({ value }) =>
              value === ''
                ? t('account:onboarding.validation.company_name_required')
                : undefined,
          }}
        >
          {({ name, state, handleChange }) => (
            <>
              <Label>{t('account:onboarding.company_name')}</Label>
              <Input
                type="text"
                name={name}
                value={state.value}
                placeholder={t('account:onboarding.company_name_placeholder')}
                onChange={(e) => handleChange(e.target.value)}
              />
              {state.meta.errors && <ErrorMessage errors={state.meta.errors} />}
            </>
          )}
        </form.Field>
        <form.Field
          name="companySize"
          validators={{
            onChange: ({ value }) => {
              if (value === null || value === undefined) return undefined
              const numValue = Number(value)
              if (isNaN(numValue) || numValue <= 0)
                return t('account:onboarding.validation.company_size_positive')
              return undefined
            },
          }}
        >
          {({ name, state, handleChange }) => (
            <>
              <Label>{t('account:onboarding.company_size')}</Label>
              <Input
                type="number"
                name={name}
                value={state.value || ''}
                placeholder={t('account:onboarding.company_size_placeholder')}
                onChange={(e) => {
                  const value = e.target.value
                  handleChange(value === '' ? null : Number(value))
                }}
              />
              {state.meta.errors && <ErrorMessage errors={state.meta.errors} />}
            </>
          )}
        </form.Field>
        <form.Field
          name="country"
          validators={{
            onChange: ({ value }) =>
              value === ''
                ? t('account:onboarding.validation.country_required')
                : undefined,
          }}
        >
          {({ state, handleChange }) => (
            <>
              <Label>{t('account:onboarding.country')}</Label>
              <SearchableSelect
                options={countries.map(({ name, code }: Country) => ({
                  label: name,
                  value: code,
                }))}
                value={state.value}
                onChange={(value) => handleChange(value)}
                placeholder={t('account:onboarding.country_placeholder')}
              />
              {state.meta.errors && <ErrorMessage errors={state.meta.errors} />}
            </>
          )}
        </form.Field>
        <hr />
        <footer className="flex flex-row-reverse py-3">
          <form.Subscribe
            selector={(state) => [!state.canSubmit, state.isSubmitting]}
            children={([cannotSubmit, isSubmitting]) => (
              <Button
                variant="default"
                type="submit"
                disabled={cannotSubmit || isSubmitting}
              >
                {isSubmitting
                  ? t('common:general.loading')
                  : t('account:onboarding.continue')}
              </Button>
            )}
          />
        </footer>
      </form>
    </div>
  )
}

export default BasicDetails
